import styled, { css } from 'styled-components';

// props 필터링
const shouldForwardProp = prop =>
  ![
    'variant',
    'color',
    'size',
    'disabled',
    'messageStatus',
    'maxWidth',
    'isDate',
    'isNumber',
    'disabledBackground',
  ].includes(prop);

// 사이즈별 스타일 정의
const getSizeStyles = size => {
  const sizes = {
    large: {
      padding: '16px',
      fontSize: '16px',
      lineHeight: '24px',
    },
    medium: {
      padding: '12px',
      fontSize: '14px',
    },
    small: {
      padding: '8px',
      fontSize: '14px',
      lineHeight: '20px',
      fontWeight: '500',
    },
  };
  return sizes[size] || sizes.medium;
};

// 사이즈별 스타일 정의
const getLabelSizeStyles = size => {
  const sizes = {
    large: {
      fontSize: '16px',
    },
    medium: {
      fontSize: '14px',
    },
    small: {
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: '400',
    },
  };
  return sizes[size] || sizes.medium;
};

// 메시지 상태별 스타일
const getMessageStatusStyles = messageStatus => {
  const statusColors = {
    error: 'var(--Error-500)',
    success: 'var(--Success-500)',
  };
  return statusColors[messageStatus] ? `color: ${statusColors[messageStatus]};` : '';
};

// 변형별 스타일
const getVariantStyles = (variant, color, disabled) => {
  // disabled 상태일 때 공통 스타일
  if (disabled) {
    return `
      border: 1px solid var(--GrayScale-400);
      background: var(--GrayScale-100);
      input, textarea {
        background: var(--GrayScale-100);
        border: 1px solid var(--GrayScale-400);
      }
    `;
  }

  const variantStyles = {
    outline: `
      background: var(--White);
      input, textarea {
        background: var(--White);
        border: 1px solid var(--GrayScale-500);
        ${color === 'primary' && '&:focus { outline: 1px solid var(--Primary-500); }'}
        ${
          color === 'red' &&
          `
          border: 1px solid var(--Error-500);
          &:focus { outline: 1px solid var(--Error-500); }
        `
        }
      }
    `,
    contained: `
      ${(() => {
        switch (color) {
          case 'primary':
            return `
              border: 1px solid var(--Primary-500);
              background: var(--Primary-500);
            `;
          case 'secondary':
            return `
              border: 1px solid var(--GrayScale-400);
              background: var(--Primary-100);
            `;
          case 'error':
            return `
              border: 1px solid var(--Error-500);
              background: var(--Error-500);
            `;
          case 'success':
            return `
              border: 1px solid var(--Success-500);
              background: var(--Success-500);
            `;
          default:
            return `
              border: 1px solid var(--GrayScale-400);
              background: var(--GrayScale-100);
            `;
        }
      })()}
      input, textarea {
        background: ${(() => {
          switch (color) {
            case 'primary':
              return 'var(--Primary-500)';
            case 'secondary':
              return 'var(--Secondary-500)';
            case 'error':
              return 'var(--Error-500)';
            case 'success':
              return 'var(--Success-500)';
            default:
              return 'var(--GrayScale-100)';
          }
        })()};
      }
    `,
    typeRest: `
      border: none;
      background: var(--White);

    `,
    login: `
      border: none;
      background: var(--GrayScale-200);
      padding: 10px 20px;
      border-radius: 8px;
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      line-height: 24px;
      &::placeholder {
        color: var(--GrayScale-600);
      }

      
    `,
  };

  return variantStyles[variant] || '';
};

export const TextFieldStyled = styled.div.withConfig({ shouldForwardProp })`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: ${({ width }) => width};

  ${({ disabledBackground }) =>
    disabledBackground &&
    css`
      background: var(--GrayScale-200);
      padding: 8px;
      border-radius: 5px;
    `}

  .label {
    ${props => getLabelSizeStyles(props.size)}
    color: var(--GrayScale-700);
  }

  .inputWrapper {
    position: relative;
    width: 100%;
  }

  input,
  textarea {
    width: 100%;
    border: 1px solid var(--GrayScale-300);
    border-radius: 5px;
    -webkit-appearance: none;
    appearance: none;

    &:focus {
      outline: none;
      border-color: var(--Primary-500);
    }

    &::placeholder {
      color: var(--GrayScale-500);
      opacity: 1;
    }

    ${({ disabled }) =>
      disabled &&
      css`
        background: var(--GrayScale-100);
        cursor: not-allowed;
        -webkit-text-fill-color: var(--GrayScale-700);
        opacity: 1;
      `}
  }

  .message {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;

    span {
      ${({ messageStatus }) => {
        switch (messageStatus) {
          case 'error':
            return css`
              color: var(--Error-500);
            `;
          case 'success':
            return css`
              color: var(--Success-500);
            `;
          case 'basic':
            return css`
              color: var(--Primary-500);
            `;
          default:
            return css`
              color: var(--GrayScale-700);
            `;
        }
      }}
    }

    img {
      width: 16px;
      height: 16px;
    }
  }

  ${({ variant, color, disabled, size }) => css`
    input,
    textarea {
      ${getSizeStyles(size)}
      ${getVariantStyles(variant, color, disabled)}
    }
  `}
`;
